/* eslint-disable no-param-reassign */
/* eslint-disable consistent-return */
/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';

import { useOrderFormStore } from '@/stores/forms/orderFormStore';
import { useVerificationStore } from '@/stores/forms/verificationFormStore';
import { useTrackingStore } from '@/stores/tracking/trackingStore';
import { useIdentityConfirmationStore } from '@/stores/forms/identityConfirmationFormStore';
import { useGlobalStore } from '@/stores/globalStore';
import useLocalStorage from '@/composables/storage/useLocalStorage';
import useIdentityQueueEndpoints from '@/composables/api/useIdentityQueueEndpoints';
import useIdentityQueueHelpers from '@/composables/store/identity/useIdentityQueueHelpers';
import useIdentityProgressHelpers from '@/composables/store/identity/useIdentityProgressHelpers';
import useApiHelpers from '@/composables/api/helpers';
import useOrderEndpoints from '@/composables/api/useOrderEndpoints';
import { isExpressCheckoutEnabledForMerchAndOrder } from '@/composables/router/handleExpressCheckout';
import {
  trackIdentityCompleted,
  trackIdentityStarted,
  trackIdentityFlowEnded,
} from '@/composables/tracking/useConversionTracking';

import {
  INCOMPLETE_STATUES,
  IDENTITY_BLOCKERS,
  INCOMPLETE_PAYMENT_STATUES,
  useLegacyIdentity,
} from '@/composables/store/identity/constants';
import {
  collectDobOnPaymentPage,
  filterQueueRoutesForPaymentDelegation,
  personalDetailsHasAnyPaymentItem,
  shouldDeferTinToPayment,
  PERSONAL_DETAILS_ROUTE_PATH,
  TIN_ROUTE_PATH,
} from '@/composables/store/identity/identityQueuePaymentDelegation';
import useAxnab from '@/composables/identity/useAxnab';

const { getPendingIdentityDocuments } = useIdentityQueueEndpoints();
const { checkLocalStorage, bindLocalStorage, removeLocalStorageByKey } = useLocalStorage();
const { organizeRoutes, sortRoutes, toResetIdentityQueue } = useIdentityQueueHelpers;
const {
  handleIdentityProgressDoc,
  completePreviousIdentityDocs,
} = useIdentityProgressHelpers;
const { getRequest, postRequest } = useApiHelpers();
const { getOrderId } = useOrderEndpoints();
const {
  hasAxnabInQueue,
  isAxnab,
  handleAxnabModal,
  toggleAxnabStorage,
} = useAxnab();

export const useIdentityStore = defineStore({
  id: 'identityStore',
  state: () => ({
    identityRoutes: checkLocalStorage('identityRoutes', []), // for ID queue
    queueCounter: checkLocalStorage('queueCounter', 1),
    totalSteps: checkLocalStorage('totalSteps', 1),
    requiresDocRecollection: checkLocalStorage('requiresDocRecollection', false),
    hasSubmittedToSumsub: false,
    identityProgress: checkLocalStorage('identityProgress', []), // for ID dashboard
    currentIdentityView: '',
    /** Set from latest queue API in setupIdentityQueue; not persisted. */
    deferTinToPaymentCollection: false,
    /** DOB collected on Primer when PD route skipped but DOB is still BANXA. */
    deferDobToPaymentCollection: false,
    /** Any PD item is PAYMENT — PD identity route should be skipped. */
    personalDetailsDeferredToPayment: false,
  }),
  actions: {
    async fetchIdentityDocuments() {
      const { data } = await getPendingIdentityDocuments();
      if (hasAxnabInQueue(data)) {
        toggleAxnabStorage(true);
        handleAxnabModal();
        return;
      }

      return data;
    },
    async setupIdentityQueue(fromPath) {
      const raw = await this.fetchIdentityDocuments();
      let data = raw;
      if (typeof raw === 'string') {
        try {
          data = JSON.parse(raw);
        } catch (e) {
          return Promise.reject(raw);
        }
      }
      const newDocuments = [];

      return new Promise((res, rej) => {
        if (!data || typeof data !== 'object') {
          this.deferTinToPaymentCollection = false;
          this.deferDobToPaymentCollection = false;
          this.personalDetailsDeferredToPayment = false;
          rej(data);
          return;
        }

        Object.entries(data).forEach(([key, value], index) => {
          organizeRoutes({
            self: this,
            key,
            value,
            overrideRoutes: newDocuments,
            index,
          });
        });

        this.deferTinToPaymentCollection = shouldDeferTinToPayment(data);
        this.deferDobToPaymentCollection = collectDobOnPaymentPage(data);
        this.personalDetailsDeferredToPayment = personalDetailsHasAnyPaymentItem(
          data.PERSONAL_DETAILS,
        );

        const filteredDocuments = filterQueueRoutesForPaymentDelegation(
          newDocuments,
          data,
        );

        // 1st condition: from non identity pages, we can call the queue and it
        // should override the queue array
        // 2nd condition: from sumsub pages we can have the queue overridden
        // by sumsub recollection requirement
        // 3rd condition: we have payment based exceptions like dlocal which can
        // trigger the queue again to show a dlocal based page

        if (
          (fromPath && !fromPath.includes('identity'))
          || this.requiresDocRecollection
          || toResetIdentityQueue.value
        ) {
          this.identityRoutes = filteredDocuments;
          this.resetCounter();
        }
        this.identityRoutes = sortRoutes(this.identityRoutes);
        this.calculateTotalSteps();
        bindLocalStorage('identityRoutes', this.identityRoutes);
        res(data);
      });
    },
    calculateTotalSteps() {
      const numberOfBanxaSteps = this.identityRoutes.reduce((acc, identity) => {
        if (identity.provider.toUpperCase() === 'BANXA') {
          acc += 1;
        }
        return acc;
      }, 0);

      // we count sumsub and plaid items as 1 as all documents are collected in one page
      const numberOfSumsubSteps = this.identityRoutes.some(
        (identity) => identity.provider.toUpperCase() === 'SUMSUB',
      ) ? 1
        : 0;
      const numberOfPlaidSteps = this.identityRoutes.some(
        (identity) => identity.provider.toUpperCase() === 'PLAID',
      ) ? 1
        : 0;
      const numberOfIDVSteps = numberOfSumsubSteps + numberOfPlaidSteps;
      const totalSteps = numberOfBanxaSteps + numberOfIDVSteps;

      if (totalSteps === 0) {
        this.totalSteps = 1;
        return;
      }

      this.totalSteps = numberOfBanxaSteps + numberOfIDVSteps;
      bindLocalStorage('totalSteps', this.totalSteps);
    },
    async preFetchNextRoute(route = null) {
      if (await useLegacyIdentity()) {
        // There might be occasion where we receive two payloads from
        // sumsub. In that case, for the second payload, we have to make
        // sure we don't update the queue counter.
        if (this.hasSubmittedToSumsub && route?.name.includes('sumsub')) {
          return;
        }
        this.queueCounter += 1;
        bindLocalStorage('queueCounter', this.queueCounter);
        return;
      }

      if (route.name.includes('sumsub')) {
        this.identityProgress.map((identityDoc) => {
          if (identityDoc.url.includes('sumsub')) {
            identityDoc.status = 'completed';
          }
          return identityDoc;
        });
      } else if (route.name.includes('plaid')) {
        this.identityProgress.map((identityDoc) => {
          if (identityDoc.url.includes('plaid')) {
            identityDoc.status = 'completed';
          }
          return identityDoc;
        });
      } else {
        this.identityProgress.map((identityDoc) => {
          if (identityDoc.title === this.currentIdentityView) {
            identityDoc.status = 'completed';
          }
          return identityDoc;
        });
      }

      bindLocalStorage('identityProgress', this.identityProgress);
    },
    setCurrentIdentityPageStatus(status) {
      const docToMarkSubmitting = this.identityProgress.filter(
        (identityDoc) => identityDoc.title === this.currentIdentityView,
      );

      if (docToMarkSubmitting.length > 0) {
        docToMarkSubmitting[0].status = status;
      }
      bindLocalStorage('identityProgress', this.identityProgress);
    },
    updateCurrentIdentityView(currentView) {
      this.currentIdentityView = currentView;
    },
    updateToPreviousRoute() {
      this.queueCounter -= 1;
      bindLocalStorage('queueCounter', this.queueCounter);
    },
    resetCounter() {
      this.queueCounter = 1;
      bindLocalStorage('queueCounter', this.queueCounter);
    },
    resetSteps() {
      this.totalSteps = 1;
      bindLocalStorage('totalSteps', this.totalSteps);
    },

    async handleDocumentRecollectionChange() {
      const orderId = await getOrderId();
      // Assuming that the order is not registered yet.
      if (!orderId) {
        return '/payment';
      }
      const statusRes = await getRequest(`/status/${orderId}`);
      const paymentStatus = statusRes?.data?.data?.payment?.label;

      // skip routing to status page if payment is pending and route to payement page
      if ((paymentStatus && paymentStatus.toLowerCase().includes('pending'))) {
        return '/payment';
      }
      return `/status/${orderId}`;
    },

    async fetchNextIdentityRoute(route) {
      trackIdentityStarted();
      const globalStore = useGlobalStore();
      const orderStore = useOrderFormStore();
      const trackingStore = useTrackingStore();
      const verificationStore = useVerificationStore();
      const identityConfStore = useIdentityConfirmationStore();
      const userKYCDetails = {
        countryResidence: identityConfStore?.form?.country?.value
        || verificationStore?.form?.country?.value,
        state: identityConfStore?.form?.state?.value,
        subdivision: verificationStore?.form?.subdivision?.value,
      };

      // Push to payment and let the payment route deal with the routing.
      const paymentRoute = '/payment';
      const orderId = await getOrderId();

      return this.setupIdentityQueue(route?.path).then(async (identityDocs) => {
        const noRemainingIDDocsFromApi = !identityDocs || identityDocs.length <= 0;

        /*
          axnab throws a modal to which we want to prevent any routing in the background as
          it will redirect to a 3rd party link and we don't want to redirect to any other routes
        */
          if (hasAxnabInQueue(identityDocs)) {
          return;
        }

        if (
            noRemainingIDDocsFromApi
          || this.identityRoutes.length === 0
          || (orderStore.getActivePaymentByMode
            && this.queueCounter > this.totalSteps)
        ) {
          trackIdentityFlowEnded();
          // on document recollection we route straight to /status page
          if (this.requiresDocRecollection) {
            trackingStore.recordAction(
              'SUMSUB RECOLLECTION - ID QUEUE',
              userKYCDetails,
            );
            const documentRecollectionRoute = await this.handleDocumentRecollectionChange();
            return documentRecollectionRoute;
          }

          // @deprecated EXPRESS_CHECKOUT — block is unreachable because
          // `isExpressCheckoutEnabledForMerchAndOrder()` always returns false
          // (and `canSkipVerification` is only ever set inside other express-only
          // paths). This was meant to short-circuit the post-payment verification
          // bounce to `/status/<orderId>`. Remove with <TICKET>.
          if (isExpressCheckoutEnabledForMerchAndOrder()
            && verificationStore.canSkipVerification
          ) {
            if (!orderId) {
              trackingStore.recordAction(
                'EXPRESS_CHECKOUT - Order registered & Redirecting to status page',
              );

              const res = await postRequest('/orders/express-payment/buy', {});
              const registeredOrderId = res.data.order.id;
              return `/status/${registeredOrderId}`;
            }

            trackingStore.recordAction('EXPRESS_CHECKOUT - Redirecting to status page');

            return `/status/${orderId}`;
          }

          trackingStore.recordAction('FINISHED IDENTITY QUEUE', userKYCDetails);
          // For corporate merchants, mark identity as completed and check for order
          if (globalStore.getIsKyb && !orderId) {
              // Returning user without any order data - redirect to buy form
              trackingStore.recordAction(
                'CORPORATE - Returning user, redirecting to buy form',
              );
              return '/';
          }
          return paymentRoute;
        }

        const nextUrl = this.identityRoutes[this.queueCounter - 1]?.url;
        if (nextUrl === TIN_ROUTE_PATH && this.deferTinToPaymentCollection) {
          return paymentRoute;
        }
        if (nextUrl === PERSONAL_DETAILS_ROUTE_PATH && this.personalDetailsDeferredToPayment) {
          return paymentRoute;
        }

        trackIdentityCompleted();
        return nextUrl;
      }, () => {
        if (isAxnab()) {
          return;
        }
        return paymentRoute;
      });
    },
    toggleDocumentRecollection(isRequired) {
      this.requiresDocRecollection = isRequired;
      bindLocalStorage('requiresDocRecollection', this.requiresDocRecollection);
    },

    async initProgressStore() {
      const data = await this.fetchIdentityDocuments();
      const newQueueDocs = Object.entries(data);

      newQueueDocs.forEach(([key, value], index) => {
        handleIdentityProgressDoc(
          this,
					key,
					value,
					index,
				);
			});

      // check for docs after queue is called and then prev doocs are to be set as completed
      if (this.currentIdentityView) {
        completePreviousIdentityDocs(this.identityProgress, newQueueDocs);
      }

      bindLocalStorage('identityProgress', this.identityProgress);
      return newQueueDocs;
    },
    resetIdentityProgress() {
      this.identityProgress = [];
      removeLocalStorageByKey('identityProgress');
    },
    resetIdentityQueue() {
      this.identitProgress = [];
      removeLocalStorageByKey('identityQueue');
    },
    changeIdentityStatus(idDoc, status) {
      idDoc.status = status;
    },
  },
  getters: {
    /**
     * Queue `components` for the current identity step (`collection_rule`, `items`, etc.).
     * @returns {Object|undefined} API components payload, or undefined if missing / no active step.
     */
    currentIdentityStepComponents: (state) => {
      const idx = state.queueCounter - 1;
      if (idx < 0 || idx >= state.identityRoutes.length) {
        return undefined;
      }
      return state.identityRoutes[idx]?.components;
    },
    canContinueToPayment() {
      return this.identityProgress
        && this.identityProgress.length > 0
        && this.identityProgress.every(
          (doc) => !INCOMPLETE_STATUES.includes(doc.status),
      );
    },
    getBlockedIdentity() {
      const blockedIdentityExists = this.identityProgress.find(
        (docs) => IDENTITY_BLOCKERS.includes(docs.centryKey),
      );
      return blockedIdentityExists;
    },

    async userHasPaidOnce() {
      if (!this.canContinueToPayment) {
        return false;
      }

      const orderId = await getOrderId();
      if (!orderId) {
        return false;
      }
      const statusRes = await getRequest(`/status/${orderId}`);
      const paymentStatus = statusRes?.data?.data?.status?.payment?.label;
      const paymentIsIncomplete = !INCOMPLETE_PAYMENT_STATUES.some(
        (status) => paymentStatus.toLowerCase().includes(status),
      );
      if (paymentStatus && paymentIsIncomplete) {
        return true;
      }

      return false;
    },

    getIdentityProgressByRoute: (store) => (identityRoute) => {
      if (!identityRoute || store.identityProgress.length <= 0) {
        return false;
      }

      return store.identityProgress.find(
        (idDoc) => idDoc?.url.toLowerCase() === identityRoute.toLowerCase(),
      );
    },
  },
});
